import type {
  ReviewAggregateSummary,
  ReviewEligibilityResponse,
} from "@/actions/review";
import { cn } from "@/lib/utils";
import { ReviewStars } from "./review-stars";
import { ReviewWriteButton } from "./review-write-button.client";

type ReviewSummaryProps = {
  title: string;
  subtitle: string;
  writeHref: string;
  aggregate: ReviewAggregateSummary;
  eligibility: ReviewEligibilityResponse | null;
  /** Selects a rating filter client-side; pass `null` to clear. */
  onSelectRating?: (rating: number | null) => void;
  activeRating?: number | null;
  onWriteClick?: () => void;
  canReview?: boolean;
  isAuthenticated?: boolean;
  showWriteButton?: boolean;
};

const CARD_CLASS =
  "rounded-[24px] border border-white/15 bg-white/40 p-4 shadow-lg backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.03] dark:shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]";

function subratingLabel(key: string) {
  return key.replace(/_/g, " ");
}

export function ReviewSummary({
  title: _title,
  writeHref,
  aggregate,
  eligibility,
  onSelectRating,
  activeRating = null,
  onWriteClick: _onWriteClick,
  canReview: canReviewProp,
  isAuthenticated = false,
  showWriteButton = true,
}: ReviewSummaryProps) {
  const canReview =
    canReviewProp !== undefined
      ? canReviewProp
      : Boolean(eligibility?.allowed || eligibility?.existingReviewPublicId);
  const writeLabel = eligibility?.existingReviewPublicId
    ? "Edit your review"
    : "Write a review";
  const hasReviews = aggregate.publishedCount > 0;
  const showHistogram = aggregate.publishedCount >= 3;
  const subratings = Object.entries(aggregate.subratingAverages).filter(
    ([, value]) => value > 0,
  );

  return (
    <section className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-40 bg-linear-to-b from-brand/8 via-brand/[0.03] to-transparent dark:from-white/[0.04] dark:to-transparent" />
      <div>
        {hasReviews ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-4xl leading-none font-semibold text-text-primary tabular-nums dark:text-white">
              {aggregate.averageRating.toFixed(1)}
            </span>
            <div className="flex flex-col gap-1">
              <ReviewStars rating={aggregate.averageRating} size="md" />
              <span className="text-sm text-text-secondary dark:text-white/60">
                {aggregate.publishedCount} review
                {aggregate.publishedCount === 1 ? "" : "s"}
                {aggregate.verifiedCount > 0
                  ? ` · ${aggregate.verifiedCount} verified`
                  : ""}
              </span>
            </div>
          </div>
        ) : null}

        {hasReviews && showWriteButton && (
          <ReviewWriteButton
            canReview={canReview}
            writeHref={writeHref}
            writeLabel={writeLabel}
            blockedReason={
              eligibility?.reason ?? "Sign in to review this page."
            }
            isAuthenticated={isAuthenticated}
            size="lg"
            className="w-full"
          />
        )}

        {showHistogram || subratings.length > 0 ? (
          <div className={cn(CARD_CLASS, "space-y-5")}>
            {showHistogram ? (
              <RatingHistogram
                histogram={aggregate.histogram}
                total={aggregate.publishedCount}
                onSelectRating={onSelectRating}
                activeRating={activeRating}
              />
            ) : null}

            {showHistogram && subratings.length > 0 ? (
              <div className="border-t border-border/60 dark:border-glass-border-10" />
            ) : null}

            {subratings.length > 0 ? (
              <div className="space-y-3">
                <h2 className="text-xs font-semibold tracking-wider text-text-tertiary uppercase">
                  Rated by category
                </h2>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  {subratings.map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between gap-3"
                    >
                      <dt className="truncate text-sm text-text-secondary capitalize dark:text-white/70">
                        {subratingLabel(key)}
                      </dt>
                      <dd className="flex shrink-0 items-center gap-2">
                        <ReviewStars rating={value} size="sm" />
                        <span className="w-7 text-right text-sm font-semibold text-text-primary tabular-nums dark:text-white">
                          {value.toFixed(1)}
                        </span>
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function RatingHistogram({
  histogram,
  total,
  onSelectRating,
  activeRating,
}: {
  histogram: Record<string, number>;
  total: number;
  onSelectRating?: (rating: number | null) => void;
  activeRating?: number | null;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold tracking-wider text-text-tertiary uppercase">
          Rating breakdown
        </h2>
        {activeRating && onSelectRating ? (
          <button
            type="button"
            onClick={() => onSelectRating(null)}
            className="text-xs font-medium text-brand hover:underline"
          >
            Clear filter
          </button>
        ) : null}
      </div>
      {[5, 4, 3, 2, 1].map((rating) => {
        const count = histogram[String(rating)] ?? 0;
        const width = total > 0 ? (count / total) * 100 : 0;
        const isActive = activeRating === rating;
        const rowClass = cn(
          "grid w-full grid-cols-[2.75rem_1fr_2rem] items-center gap-2 rounded-lg py-0.5 text-left text-sm transition",
          onSelectRating &&
            "hover:bg-surface-alt/60 active:scale-[0.99] dark:hover:bg-white/[0.06]",
          isActive && "font-semibold",
        );
        const content = (
          <>
            <span className="text-text-secondary tabular-nums dark:text-white/70">
              {rating} star
            </span>
            <span className="h-2 overflow-hidden rounded-full bg-surface-alt dark:bg-glass-bg-10">
              <span
                className={cn(
                  "block h-full rounded-full bg-amber-500 transition-[width] duration-500",
                  isActive && "bg-amber-400",
                )}
                style={{ width: `${width}%` }}
              />
            </span>
            <span className="text-right text-text-secondary tabular-nums dark:text-white/70">
              {count}
            </span>
          </>
        );

        return onSelectRating ? (
          <button
            key={rating}
            type="button"
            onClick={() => onSelectRating(isActive ? null : rating)}
            className={rowClass}
            aria-label={`Show ${rating} star reviews`}
            aria-pressed={isActive}
          >
            {content}
          </button>
        ) : (
          <div key={rating} className={rowClass}>
            {content}
          </div>
        );
      })}
    </div>
  );
}
